import React from 'react';
import { alpha, ButtonBase, CircularProgress, Tooltip } from '@mui/material';
import { Whatshot } from '@mui/icons-material';

interface MomentumToggleProps {
  on: boolean;
  onChange: (on: boolean) => void;
  loading?: boolean;
  /** Why the button is unavailable; disables it when set. */
  disabledReason?: string;
  /** small matches the small segmented toggles (26px thumb). */
  size?: 'small' | 'medium';
}

/** Fire icon button for the Momentum view (▲/▼ show how much recent form moves each driver); lit up when on. */
export const MomentumToggle: React.FC<MomentumToggleProps> = ({ on, onChange, loading, disabledReason, size = 'medium' }) => {
  const active = on && !disabledReason;
  const px = size === 'small' ? 34 : 40;
  return (
    <Tooltip title={disabledReason ?? 'Momentum view: See how much recent form impacts overall ratings'}>
      <span>
        <ButtonBase
          role="switch"
          aria-checked={active}
          aria-label="Momentum view"
          disabled={!!disabledReason}
          onClick={() => onChange(!on)}
          sx={(theme) => ({
            // Same height and outline as the segmented toggles beside it (3px padding around a 32px thumb, 26px when small).
            width: px,
            height: px,
            flexShrink: 0,
            borderRadius: 999,
            border: `1px solid ${theme.palette.divider}`,
            transition: theme.transitions.create(['background-color', 'color', 'box-shadow'], { duration: 150 }),
            color: active ? (theme.palette.mode === 'dark' ? '#0d1f12' : theme.palette.primary.contrastText) : theme.palette.text.secondary,
            bgcolor: active
              ? theme.palette.primary.main
              : theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.04)'
                : 'rgba(0, 0, 0, 0.03)',
            boxShadow: active ? `0 0 12px ${alpha(theme.palette.primary.main, 0.35)}` : 'none',
            '&:hover': {
              color: active ? undefined : theme.palette.text.primary,
              bgcolor: active ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.1),
            },
            '&.Mui-disabled': { opacity: 0.45 },
            '&.Mui-focusVisible': { outline: `2px solid ${alpha(theme.palette.primary.main, 0.6)}`, outlineOffset: 1 },
          })}
        >
          {loading && active ? (
            <CircularProgress size={size === 'small' ? 15 : 18} color="inherit" />
          ) : (
            <Whatshot sx={{ fontSize: size === 'small' ? 17 : 20 }} />
          )}
        </ButtonBase>
      </span>
    </Tooltip>
  );
};
